import React, {useMemo} from 'react';
import {useGetIdsEc, useLogActivity} from '../../../Domains/Auth/Hooks/UserManager';
import Loading from '../../common/components/Loading';
import {ColumnDef} from '@tanstack/react-table';
import {ActivityLog, UserEC} from '../../../Domains/Auth/Types/User';
import {formatDate} from '../../common/tools/date';
import TableMetronic from '../../common/components/table/TableMetronic';
import {useIntl} from 'react-intl';

const LogManager = (): JSX.Element => {
  const {isLoading: isLogLoading, data: logs} = useLogActivity();
  const logIds: number[] = Array.isArray(logs) ? logs.map((log) => log.causer_id) : [];

  const {data: users} = useGetIdsEc(logIds);
  const usersById: Record<number, UserEC> = useMemo(() => {
    if (!users) return {};

    return users.reduce((acc, user) => {
      acc[user.id] = {
        id: user.id,
        mail: user.mail,
        entity_id: String(user.entity_id),
        first_name: user.first_name,
        name: user.name,
      };
      return acc;
    }, {} as Record<number, UserEC>);
  }, [users]);
  const intl = useIntl();
  const isAllLoaded = (): boolean => {
    return !isLogLoading;
  };

  const columns = useMemo<ColumnDef<ActivityLog>[]>(
    () => [
      {
        header: 'Date',
        accessorKey: 'date',
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        header: intl.formatMessage({id: 'CLIENT.ID.LOG'}),
        accessorKey: 'properties',
        cell: (info) => {
          const props = info.row.original.properties as Record<string, unknown> | null;

          if (!props || !props['entity_id']) return '-';
          if (!props || !props['mail']) return '-';

          return (
            <div className="flex flex-wrap gap-1">
              {String(props['entity_id']) + ' - ' + String(props['mail'])}
            </div>
          );
        },
      },
      {
        header: intl.formatMessage({id: 'HEADER.LOG.CAUSER'}),
        accessorKey: 'causer_id',
        cell: (info) => {
          const causerId = info.row.original.causer_id;
          if (!causerId) return '-';

          const user = usersById[causerId];
          if (!user) return '-';
          return <div className="flex flex-col">{user.entity_id + ' - ' + user.mail}</div>;
        },
      },

      {
        header: 'Description',
        accessorKey: 'description',
        cell: (info) => {
          const descriptionKey = info.row.original.description;

          return intl.formatMessage({
            id: descriptionKey,
            defaultMessage: descriptionKey, // fallback si la clé n'existe pas
          });
        },
      },
    ],
    [intl, usersById]
  );

  const renderMobileCard = (log: ActivityLog): JSX.Element => {
    const props = log.properties as Record<string, unknown> | null;
    const clientLabel =
      props && props['entity_id'] && props['mail']
        ? `${String(props['entity_id'])} - ${String(props['mail'])}`
        : '-';
    const user = log.causer_id ? usersById[log.causer_id] : undefined;
    const causerLabel = user ? `${user.entity_id} - ${user.mail}` : '-';
    const descriptionKey = log.description;
    const description = intl.formatMessage({
      id: descriptionKey,
      defaultMessage: descriptionKey,
    });

    return (
      <div className="d-flex flex-column gap-2">
        <div className="d-flex align-items-center justify-content-between">
          <span className="fw-bold">{formatDate(log.created_at)}</span>
        </div>
        <div className="d-flex align-items-start justify-content-between gap-2">
          <span className="text-muted flex-shrink-0">
            {intl.formatMessage({id: 'CLIENT.ID.LOG'})}
          </span>
          <span style={{minWidth: 0, wordBreak: 'break-word'}} className="text-end">
            {clientLabel}
          </span>
        </div>
        <div className="d-flex align-items-start justify-content-between gap-2">
          <span className="text-muted flex-shrink-0">
            {intl.formatMessage({id: 'HEADER.LOG.CAUSER'})}
          </span>
          <span style={{minWidth: 0, wordBreak: 'break-word'}} className="text-end">
            {causerLabel}
          </span>
        </div>
        <div>
          <span className="text-muted fs-8 fw-semibold text-uppercase d-block mb-1">
            Description
          </span>
          <span style={{wordBreak: 'break-word'}}>{description}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      {!isAllLoaded() && <Loading />}
      <TableMetronic
        title={intl.formatMessage({id: 'TITLE.LOG.TABLE'})}
        data={logs ?? []}
        columns={columns}
        renderMobileCard={renderMobileCard}
        pagination
        searchBar={true}
        defaultSorting={[{id: 'date', desc: true}]}
      />
    </>
  );
};

export default LogManager;
